export const COMPANY_FACTS = [
  { label: "Years operating", value: "10+", detail: "A decade of continuous client relationships." },
  { label: "Projects delivered", value: "250+", detail: "Across Retail, E-Commerce, Fintech, Healthcare, and Education." },
  { label: "Lives impacted", value: "400k+", detail: "People reached through software Exec9 has built." },
  // KpiCounter parses one leading number plus a trailing suffix ("250+", "400k+"). A value like
  // "5.0 / 5" parses as 5.05 with a " / " suffix, so the rating is stated as its star value alone.
  { label: "Client rating", value: "5.0", detail: "Across verified client reviews on Clutch." },
] as const;

export const VALUES = [
  {
    title: "Trust",
    body: "Clients own their code and the outcome outright — no external control, no vendor lock-in. Trust, learning, honesty, and co-operation are the pillars that sit at the core of what Exec9 does.",
  },
  {
    title: "Full ownership",
    body: "Exec9 delivers full software ownership rather than renting access to a platform. Every client keeps complete rights to what is built for them, including the source code.",
  },
  {
    title: "One connected team",
    body: "Architecture, engineering, design, QA, and project management sit on one pipeline rather than being handed between separate vendors, so nothing falls between the layers.",
  },
  {
    title: "Everyday growth",
    body: "The team is supported so their skills never stop growing, and a healthy personal life is treated as being just as important as time spent in the office.",
  },
] as const;

export const PROCESS_STEPS = [
  {
    name: "Discover",
    text: "Exec9 defines the problem, scope, and success criteria with you before any code is written, producing a concrete timeline and cost estimate.",
  },
  {
    name: "Design",
    text: "The team establishes the system architecture and interface design, so engineering builds against a decided structure rather than discovering it mid-project.",
  },
  {
    name: "Build",
    text: "Engineers deliver production-grade, peer-reviewed code in rapid development cycles, with working software visible to the client throughout.",
  },
  {
    name: "Ship & scale",
    text: "Exec9 ships the product to production and supports it as usage grows, handing over full ownership of the codebase to the client.",
  },
] as const;

const SITE_URL = "https://www.exec9.com";

export const ABOUT_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${SITE_URL}/about#webpage`,
  url: `${SITE_URL}/about`,
  name: "About Exec9",
  description:
    "Exec9 is a Lahore-based full-cycle software development agency with 10+ years of client relationships and 250+ projects delivered across Retail, E-Commerce, Fintech, Healthcare, and Education.",
  mainEntity: {
    "@type": "ProfessionalService",
    "@id": `${SITE_URL}/#organization`,
    name: "Exec9",
    url: SITE_URL,
    description:
      "Exec9 is a full-cycle software development agency based in Lahore, Pakistan, building AI-powered web, mobile, and blockchain products for clients worldwide.",
    logo: `${SITE_URL}/logo.svg`,
    email: "info@exec9.com",
    telephone: "+92-3028490697",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "29-A, Block A, Valencia Town",
      addressLocality: "Lahore",
      postalCode: "54770",
      addressCountry: "PK",
    },
    areaServed: "Worldwide",
    knowsAbout: [
      "Custom software development",
      "AI-powered software solutions",
      "Machine learning and NLP",
      "Blockchain and smart contract development",
      "FinTech software",
      "EdTech platforms",
      "Healthcare software",
      "Mobile app development",
      "UI/UX design",
      "Staff augmentation",
    ],
    sameAs: [
      "https://www.instagram.com/exec9",
      "https://www.facebook.com/exec9",
      "https://www.linkedin.com/company/exec9",
      "https://clutch.co/profile/exec9",
    ],
  },
};

export const PROCESS_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How Exec9 builds software",
  description:
    "Exec9's four-stage delivery process: discover the problem, design the system, build production-grade code, then ship and scale it.",
  step: PROCESS_STEPS.map((step, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: step.name,
    text: step.text,
    url: `${SITE_URL}/about#process`,
  })),
};

export const ABOUT_BREADCRUMB_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "About", item: `${SITE_URL}/about` },
  ],
};
